
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Layout from '@/components/Layout';
import { CheckCircle, Calendar, CreditCard, Share2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const Confirmation = () => {
  // Mock confirmation data - in real app this would come from state/props
  const confirmationType = 'appointment' as 'appointment' | 'credit_purchase' | 'gift_card';
  
  const confirmationData = {
    appointment: {
      id: 'APT-2024-001',
      treatment: 'Botox - Forehead',
      date: '2024-01-20',
      time: '10:00 AM',
      location: 'JUV Austin - Downtown',
      provider: 'Dr. Smith',
      creditsUsed: 150
    },
    credit_purchase: {
      id: 'CR-2024-001',
      amount: 300,
      bonus: 50,
      total: 350
    },
    gift_card: {
      id: 'GC-2024-001',
      amount: 200,
      recipient: 'friend@example.com'
    }
  };

  const renderConfirmationContent = () => {
    if (confirmationType === 'appointment') {
      const apt = confirmationData.appointment;
      return (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center text-white" style={{ backgroundColor: '#10b981' }}>
              <CheckCircle className="h-10 w-10" />
            </div>
            <h2 className="text-2xl font-bold font-juv-heading" style={{ color: '#5C322B' }}>
              Appointment Confirmed!
            </h2>
            <p className="mt-2" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>
              Your appointment has been successfully booked
            </p>
          </div>

          <Card className="bg-white shadow-luxury" style={{ borderColor: 'rgba(205, 167, 157, 0.3)' }}>
            <CardHeader>
              <CardTitle className="font-juv-heading" style={{ color: '#5C322B' }}>
                Appointment Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>Treatment</p>
                  <p className="font-semibold" style={{ color: '#5C322B' }}>{apt.treatment}</p>
                </div>
                <div>
                  <p className="text-sm" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>Confirmation ID</p>
                  <p className="font-mono text-sm" style={{ color: '#5C322B' }}>{apt.id}</p>
                </div>
                <div>
                  <p className="text-sm" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>Date & Time</p>
                  <p className="font-semibold" style={{ color: '#5C322B' }}>
                    {new Date(apt.date).toLocaleDateString()} at {apt.time}
                  </p>
                </div>
                <div>
                  <p className="text-sm" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>Provider</p>
                  <p className="font-semibold" style={{ color: '#5C322B' }}>{apt.provider}</p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>Location</p>
                  <p className="font-semibold" style={{ color: '#5C322B' }}>{apt.location}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button 
              variant="outline"
              className="flex items-center justify-center space-x-2"
              style={{ borderColor: 'rgba(205, 167, 157, 0.5)', color: '#5C322B' }}
            >
              <Calendar className="h-4 w-4" />
              <span>Add to Calendar</span>
            </Button>
            <Button 
              variant="outline"
              className="flex items-center justify-center space-x-2"
              style={{ borderColor: 'rgba(205, 167, 157, 0.5)', color: '#5C322B' }}
            >
              <Share2 className="h-4 w-4" />
              <span>Share</span>
            </Button>
            <Button 
              asChild
              className="text-white"
              style={{ backgroundColor: '#A55C52' }}
            >
              <Link to="/appointments">
                <span>View All Appointments</span>
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      );
    }

    if (confirmationType === 'credit_purchase') {
      const credits = confirmationData.credit_purchase;
      return (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center text-white" style={{ backgroundColor: '#10b981' }}>
              <CreditCard className="h-10 w-10" />
            </div>
            <h2 className="text-2xl font-bold font-juv-heading" style={{ color: '#5C322B' }}>
              Credits Added!
            </h2>
            <p className="mt-2" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>
              Your glow credits have been successfully added to your account
            </p>
          </div>

          <Card className="bg-white shadow-luxury" style={{ borderColor: 'rgba(205, 167, 157, 0.3)' }}>
            <CardContent className="p-8 text-center">
              <div className="text-4xl font-bold mb-2" style={{ color: '#10b981' }}>
                +${credits.total}
              </div>
              <p className="text-sm" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>
                Credits Added (${credits.amount} purchased + ${credits.bonus} bonus)
              </p>
              <p className="text-xs mt-2" style={{ color: 'rgba(92, 50, 43, 0.5)' }}>
                Transaction ID: {credits.id}
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Default case
    return (
      <div className="text-center">
        <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center text-white" style={{ backgroundColor: '#10b981' }}>
          <CheckCircle className="h-10 w-10" />
        </div>
        <h2 className="text-2xl font-bold font-juv-heading" style={{ color: '#5C322B' }}>
          Success!
        </h2>
        <p className="mt-2" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>
          Your request has been processed successfully
        </p>
      </div>
    );
  };

  return (
    <Layout>
      <div className="min-h-screen" style={{ background: 'linear-gradient(to bottom right, #F2D9D3, #FAF8F6)' }}>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {renderConfirmationContent()}

          {/* What's Next */}
          <Card className="bg-white shadow-luxury mt-8" style={{ borderColor: 'rgba(205, 167, 157, 0.3)' }}>
            <CardHeader>
              <CardTitle className="font-juv-heading" style={{ color: '#5C322B' }}>
                What's Next?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ backgroundColor: '#A55C52' }}>
                    1
                  </div>
                  <div>
                    <p className="font-medium" style={{ color: '#5C322B' }}>Check your email</p>
                    <p className="text-sm" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>
                      We've sent you a confirmation email with all the details
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ backgroundColor: '#A55C52' }}>
                    2
                  </div>
                  <div>
                    <p className="font-medium" style={{ color: '#5C322B' }}>Prepare for your visit</p>
                    <p className="text-sm" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>
                      Review pre-care instructions and arrive 15 minutes early
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ backgroundColor: '#A55C52' }}>
                    3
                  </div>
                  <div>
                    <p className="font-medium" style={{ color: '#5C322B' }}>Need to reschedule?</p>
                    <p className="text-sm" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>
                      Contact us at least 24 hours in advance
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Button 
              variant="outline"
              asChild
              style={{ borderColor: 'rgba(205, 167, 157, 0.5)', color: '#5C322B' }}
            >
              <Link to="/dashboard">Return to Dashboard</Link>
            </Button>
            <Button 
              asChild
              className="text-white"
              style={{ backgroundColor: '#A55C52' }}
            >
              <Link to="/treatments">Browse More Treatments</Link>
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Confirmation;
